import { useEffect, useState } from 'react';
import {
  fetchAuthStatus,
  fetchHasPassword,
  loginWithPassword,
  setupPassword,
  type LoginResult,
} from '../api/auth';
import './LoginScreen.css';

interface Props {
  /**
   * v36 (2026-07-25): callback ontvangt het LoginResult zodat de parent
   * direct de is_admin flag kan opslaan zonder tweede /api/auth/status
   * roundtrip. Backward-compat: parents die het niet nodig hebben negeren
   * simpel de parameter.
   */
  onSuccess: (result?: LoginResult) => void;
  /** Site-naam in de header — uit installation.name als beschikbaar */
  siteLabel?: string;
}

type Mode = 'login' | 'setup' | 'loading';

/**
 * Authenticatie-overlay met twee scherm-modi:
 *
 * 1. **login** (default als wachtwoord ingesteld is): wachtwoord-input
 *    + "Onthoud me 30 dagen" checkbox. Browser-password-manager kan de
 *    waarde via `autocomplete="current-password"` onthouden — daarna
 *    is inloggen letterlijk 1 tap.
 *
 * 2. **setup** (eerste keer of recovery): API-key + nieuw wachtwoord.
 *    Wordt automatisch getoond bij `has_password=false` op mount, of
 *    handmatig via "Wachtwoord vergeten?" link in login-modus.
 *
 * Op mount checkt het component `/api/auth/has-password` om de juiste
 * modus te kiezen — voorkomt dat een verse Pi de gebruiker eerst een
 * leeg wachtwoord laat gokken.
 */
/**
 * Melding voor het geval dat de login slaagt maar de sessie niet blijft plakken.
 *
 * Aanleiding (2026-08-28, issue #426): de sessiecookie werd altijd met `Secure`
 * gezet, en die bewaart een browser niet over gewoon http. Op het LAN-noodpad
 * gaf de login dus 200, zette dit scherm `authed` op true, en kwam de gebruiker
 * in een dashboard dat nooit data kon halen. De backend zet de vlag nu per pad,
 * maar de aanname zelf — "200 betekent ingelogd" — was het echte defect: elke
 * toekomstige variant hiervan hoort zichtbaar te zijn in plaats van stil.
 */
const SESSIE_NIET_BEWAARD =
  'Inloggen slaagde, maar je browser bewaarde de sessie niet. Dat gebeurt als ' +
  'cookies geblokkeerd zijn, of bij een privévenster dat ze direct weggooit. ' +
  'Sta cookies toe voor deze site en probeer het opnieuw.';

export function LoginScreen({ onSuccess, siteLabel }: Props) {
  const [mode, setMode] = useState<Mode>('loading');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [apiKey, setApiKey] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchHasPassword()
      .then((s) => {
        if (cancelled) return;
        setMode(s.has_password ? 'login' : 'setup');
      })
      .catch(() => { if (!cancelled) setMode('login'); });
    return () => { cancelled = true; };
  }, []);

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault();
    if (!password) {
      setError('Voer je wachtwoord in');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await loginWithPassword(password, remember);
      // Niet op de 200 afgaan: pas als de server ons op een VOLGENDE request
      // herkent, staat de sessie er werkelijk.
      const status = await fetchAuthStatus();
      if (!status.authed) {
        setError(SESSIE_NIET_BEWAARD);
        return;
      }
      setPassword('');
      onSuccess(result);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function handleSetup(e: React.FormEvent) {
    e.preventDefault();
    if (!apiKey.trim() || !newPassword) {
      setError('Vul beide velden in');
      return;
    }
    if (newPassword.length < 8) {
      setError('Wachtwoord moet minstens 8 karakters zijn');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await setupPassword(apiKey.trim(), newPassword);
      const status = await fetchAuthStatus();
      if (!status.authed) {
        setError(SESSIE_NIET_BEWAARD);
        return;
      }
      setApiKey('');
      setNewPassword('');
      onSuccess(result);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (mode === 'loading') {
    return <div className="login-overlay" aria-busy="true" />;
  }

  return (
    <div className="login-overlay" role="dialog" aria-modal="true">
      {mode === 'login' ? (
        <form className="login-card" onSubmit={handleLogin}>
          <div className="login-header">
            <span className="login-brand">ZBS EMS</span>
            {siteLabel && <span className="login-site">{siteLabel}</span>}
          </div>
          <h2 className="login-title">Inloggen</h2>

          {/* Hidden username field zodat browser-password-managers een
              consistent (site, account) paar kunnen opslaan i.p.v. random.
              Voor v1 is "site_name" een acceptabele username — onderscheidend
              tussen VJ en NS in dezelfde browser. */}
          <input
            type="text"
            name="username"
            autoComplete="username"
            value={siteLabel ?? 'site'}
            readOnly
            hidden
          />

          <label className="login-label" htmlFor="password">Wachtwoord</label>
          <input
            id="password"
            type="password"
            className="login-input"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={busy}
            autoFocus
          />

          <label className="login-checkbox">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              disabled={busy}
            />
            <span>Onthoud me 30 dagen</span>
          </label>

          {error && <div className="login-error" role="alert">{error}</div>}

          <button type="submit" className="login-submit" disabled={busy || !password}>
            {busy ? 'Bezig…' : 'Inloggen'}
          </button>

          <button
            type="button"
            className="login-link"
            onClick={() => { setMode('setup'); setError(null); }}
            disabled={busy}
          >
            Wachtwoord vergeten? Stel opnieuw in met API-key.
          </button>
        </form>
      ) : (
        <form className="login-card" onSubmit={handleSetup}>
          <div className="login-header">
            <span className="login-brand">ZBS EMS</span>
            {siteLabel && <span className="login-site">{siteLabel}</span>}
          </div>
          <h2 className="login-title">Wachtwoord instellen</h2>
          <p className="login-hint">
            Voer eenmalig de site-API-key in en kies een wachtwoord. De key
            heb je al van de installateur. Daarna log je alleen nog met je
            wachtwoord in — de browser kan 'm onthouden zodat het 1 tap blijft.
          </p>

          <label className="login-label" htmlFor="api-key">API-key (eenmalig)</label>
          <input
            id="api-key"
            type="password"
            className="login-input"
            autoComplete="off"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            disabled={busy}
            placeholder="••••••••••••••••"
            autoFocus
          />

          <label className="login-label" htmlFor="new-password">Nieuw wachtwoord</label>
          <input
            id="new-password"
            type="password"
            className="login-input"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            disabled={busy}
            placeholder="≥ 8 karakters"
          />

          {error && <div className="login-error" role="alert">{error}</div>}

          <button
            type="submit"
            className="login-submit"
            disabled={busy || !apiKey || newPassword.length < 8}
          >
            {busy ? 'Bezig…' : 'Wachtwoord instellen + inloggen'}
          </button>

          <button
            type="button"
            className="login-link"
            onClick={() => { setMode('login'); setError(null); }}
            disabled={busy}
          >
            Terug naar inloggen
          </button>
        </form>
      )}
    </div>
  );
}
